import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
	Calculator,
	BookOpen,
	FlaskConical,
	Leaf,
	Book,
	BarChart2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { createSlug } from "../config/utils.js";

export const SubjectsData = [
	{
		name: "Math",
		icon: <Calculator className="text-red-600 w-10 h-10" />,
		subSubjects: [
			"Algebra",
			"Graphs & Coordinate Geometry",
			"Numbers & Numeration",
			"Calculus",
			"Geometry",
			"Trigonometry",
		],
	},
	{
		name: "Biology",
		icon: <Leaf className="text-green-600 w-10 h-10" />,
		subSubjects: [
			"Plant Biology",
			"Animal Biology",
			"Cell Biology",
			"Physiology & Homeostasis",
		],
	},
	{
		name: "Chemistry",
		icon: <FlaskConical className="text-sky-500 w-10 h-10" />,
		subSubjects: [
			"Fundamentals of Chemistry",
			"States of Matter & Properties",
			"Chemical Bonding & Molecular Structure",
			"Chemical Reactions & Stoichiometry",
			"Solutions & Separation Techniques",
			"Particle Motion & Diffusion",
		],
	},
	{
		name: "Literature",
		icon: <Book className="text-pink-600 w-10 h-10" />,
		subSubjects: [
			"African Poetry",
			"Non-African Poetry",
			"African Drama",
			"Non-African Drama",
			"African Prose",
			"Non-African Prose",
			"Shakespearean Texts",
		],
	},
	{
		name: "Commerce",
		icon: <BarChart2 className="text-orange-600 w-10 h-10" />,
		subSubjects: [
			"Introduction to Commerce & Business",
			"Business Structures & Organizations",
			"Trade & Markets",
			"Financial Aspects of Business",
			"Industry & Production",
			"Trade & Consumer Protection",
		],
	},
	{
		name: "English",
		icon: <BookOpen className="text-purple-600 w-10 h-10" />,
		subSubjects: [
			"Grammar & Sentence Structure",
			"Parts of Speech",
			"Figures of Speech & Literary Devices",
			"Idioms & Vocabulary",
			"Phonetics & Pronunciation",
			"Punctuation",
			"Reading & Comprehension",
		],
	},
];

export const SubjectsDataWithSlugs = SubjectsData.map((subject) => ({
	...subject,
	slug: createSlug(subject.name),
	subSubjects: subject.subSubjects.map((subSubject) => ({
		name: subSubject,
		slug: createSlug(subSubject),
	})),
}));

export default function Home() {
	return (
		<main className="flex-1">
			{/* Hero Section */}
			<section className="relative overflow-hidden bg-white">
				<div className="container mx-auto px-8 py-8 md:py-12 lg:py-16">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end">
						{/* Text Content */}
						<div>
							<h1 className="text-9xl md:text-[10rem] lg:text-[11rem] font-bold text-black mb-4 animate-fade-in" style={{fontFamily: 'Courgette'}}>
								ighota!
							</h1>
							<h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
								The Future of College Entry Exam Preparation
							</h2>
							<p className="text-xl text-gray-700 mb-8 max-w-2xl">
								Giving every West African student access to the best
								test prep and educational resources for WASSCE,
								JAMB, NECO, and much more!
							</p>
							<div className="flex flex-wrap gap-4">
								<Button
									size="lg"
									className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black font-bold px-8 shadow-lg transform hover:scale-105 transition-all duration-200"
									asChild>
									<Link to="/mission">Learn More!</Link>
								</Button>
								<Button
									size="lg"
									variant="outline"
									className="border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white shadow-lg transform hover:scale-105 transition-all duration-200"
									asChild>
									<Link to="/courses">View Courses</Link>
								</Button>
							</div>
						</div>
						{/* Background Image */}
						<div className="flex justify-center lg:justify-end">
							<img
								src="./ighota-bg.png"
								alt="Students studying"
								className="w-full max-w-2xl h-auto object-contain"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* Subjects Section */}
			<section id="courses" className="py-20 bg-gradient-to-b from-gray-50 to-white">
				<div className="container mx-auto px-4">
					<div className="text-center mb-16">
						<h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-800">
							Lessons Revolve Around 6 Areas!
						</h2>
						<p className="text-lg text-gray-700 max-w-3xl mx-auto">
							Engaging lessons across subjects like Math, Biology,
							Chemistry, and more—with new topics and content
							added regularly.
						</p>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
						{SubjectsDataWithSlugs.map((subject) => (
							<Card
								key={subject.name}
								className="hover:shadow-xl hover:-translate-y-2 transition-all duration-300 border-t-4 bg-gradient-to-br from-white to-gray-50"
								style={{
									borderTopColor:
										subject.name === "Math"
											? "#DC2626"
											: subject.name === "Biology"
												? "#16A34A"
												: subject.name === "Chemistry"
													? "#2563EB"
													: subject.name === "Literature"
														? "#DB2777"
														: subject.name === "Commerce"
															? "#EA580C"
															: "#7C3AED",
								}}>
								<CardHeader className="flex flex-col items-center gap-2 pb-2">
									<div className="p-3 rounded-full bg-gray-50">
										{subject.icon}
									</div>
									<h3 className="text-xl font-bold">
										{subject.name}
									</h3>
								</CardHeader>
								<CardContent>
									<ul className="space-y-2">
										{subject.subSubjects.map(
											(subSubject) => (
												<li
													key={subSubject.slug}
													className="flex items-center">
													<div className="w-1.5 h-1.5 rounded-full bg-gray-300 mr-2"></div>
													<Link
														to={`/courses/${subject.slug}/${subSubject.slug}`}
														className="text-gray-700 transition-colors"
														style={{
															'--hover-color': subject.name === "Math"
																? "#DC2626"
																: subject.name === "Biology"
																	? "#16A34A"
																	: subject.name === "Chemistry"
																		? "#2563EB"
																		: subject.name === "Literature"
																			? "#DB2777"
																			: subject.name === "Commerce"
																				? "#EA580C"
																				: "#7C3AED"
														}}
														onMouseEnter={(e) => e.target.style.color = e.target.style.getPropertyValue('--hover-color')}
														onMouseLeave={(e) => e.target.style.color = ''}>
														{subSubject.name}
													</Link>
												</li>
											)
										)}
									</ul>
								</CardContent>
							</Card>
						))}
					</div>
				</div>
			</section>

			{/* How It Works Section */}
			<section className="py-20 bg-gray-50">
				<div className="container mx-auto px-4">
					<div className="text-center mb-16">
						<h2 className="text-3xl md:text-4xl font-bold mb-4">
							Our Lessons!
						</h2>
						<p className="text-lg text-gray-600 max-w-3xl mx-auto">
							Our structured approach helps you master subjects
							efficiently
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
						<div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow text-center">
							<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 text-yellow-500 mb-6">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="24"
									height="24"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									className="w-8 h-8">
									<polygon points="23 7 16 12 23 17 23 7"></polygon>
									<rect
										x="1"
										y="5"
										width="15"
										height="14"
										rx="2"
										ry="2"></rect>
								</svg>
							</div>
							<h3 className="text-xl font-bold mb-3">
								Watch & Learn
							</h3>
							<p className="text-gray-600">
								Start with engaging video lessons that break
								down complex topics into easy-to-understand
								concepts
							</p>
						</div>

						<div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow text-center">
							<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-500 mb-6">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="24"
									height="24"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									className="w-8 h-8">
									<circle cx="12" cy="12" r="10"></circle>
									<path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
									<line
										x1="12"
										y1="17"
										x2="12.01"
										y2="17"></line>
								</svg>
							</div>
							<h3 className="text-xl font-bold mb-3">
								Practice & Test
							</h3>
							<p className="text-gray-600">
								Reinforce your learning with interactive quizzes
								designed to test your understanding
							</p>
						</div>

						<div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow text-center">
							<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-500 mb-6">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="24"
									height="24"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									className="w-8 h-8">
									<path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
									<path d="M6 12v5c3 3 9 3 12 0v-5"></path>
								</svg>
							</div>
							<h3 className="text-xl font-bold mb-3">
								Master Topics
							</h3>
							<p className="text-gray-600">
								Build confidence as you progress through
								increasingly challenging content
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* Benefits Section */}
			<section className="py-20 bg-white">
				<div className="container mx-auto px-4">
					<div className="max-w-6xl mx-auto">
						<div className="flex flex-col md:flex-row gap-12 items-center">
							<div className="md:w-1/2">
								<img
									src="/images/general/partner_photo.jpg"
									alt="Students learning"
									className="rounded-lg shadow-md w-full object-cover"
								/>
							</div>
							<div className="md:w-1/2">
								<h2 className="text-3xl md:text-4xl font-bold mb-6">
									Why Choose Our Courses?
								</h2>
								<ul className="space-y-4">
									<li className="flex gap-3">
										<div className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center mt-1">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="16"
												height="16"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
												className="text-yellow-500">
												<polyline points="20 6 9 17 4 12"></polyline>
											</svg>
										</div>
										<div>
											<strong className="text-lg font-semibold">
												Structured Learning Path:
											</strong>
											<p className="text-gray-600 mt-1">
												Carefully designed curriculum
												that builds from fundamentals to
												advanced concepts
											</p>
										</div>
									</li>
									<li className="flex gap-3">
										<div className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center mt-1">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="16"
												height="16"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
												className="text-yellow-500">
												<polyline points="20 6 9 17 4 12"></polyline>
											</svg>
										</div>
										<div>
											<strong className="text-lg font-semibold">
												Interactive Experience:
											</strong>
											<p className="text-gray-600 mt-1">
												Engage with content through
												videos, quizzes, and practical
												exercises
											</p>
										</div>
									</li>
									<li className="flex gap-3">
										<div className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center mt-1">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="16"
												height="16"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
												className="text-yellow-500">
												<polyline points="20 6 9 17 4 12"></polyline>
											</svg>
										</div>
										<div>
											<strong className="text-lg font-semibold">
												Self-Paced:
											</strong>
											<p className="text-gray-600 mt-1">
												Learn at your own speed with
												unlimited access to course
												materials
											</p>
										</div>
									</li>
									<li className="flex gap-3">
										<div className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center mt-1">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="16"
												height="16"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
												className="text-yellow-500">
												<polyline points="20 6 9 17 4 12"></polyline>
											</svg>
										</div>
										<div>
											<strong className="text-lg font-semibold">
												Comprehensive Coverage:
											</strong>
											<p className="text-gray-600 mt-1">
												In-depth content covering all
												major topics in each subject
											</p>
										</div>
									</li>
								</ul>
								<Button
									className="mt-8 bg-yellow-500 hover:bg-yellow-600 text-black"
									size="lg"
									asChild>
									<Link to="/courses">
										Explore All Courses
									</Link>
								</Button>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* CTA Section */}
			<section className="py-16 bg-yellow-500">
				<div className="container mx-auto px-4 text-center">
					<h2 className="text-3xl md:text-4xl font-bold mb-6 text-black">
						Ready to Start Your Learning Journey?
					</h2>
					<p className="text-xl text-black/80 mb-8 max-w-2xl mx-auto">
						Join thousands of students who have improved their exam
						scores with Ighota.
					</p>
					<div className="flex flex-wrap justify-center gap-4">
						<Button
							size="lg"
							className="bg-black hover:bg-gray-800 text-white"
							asChild>
							<Link to="/auth/register">Sign Up Now</Link>
						</Button>
						<Button
							size="lg"
							variant="outline"
							className="bg-transparent border-black text-black hover:bg-black/10"
							asChild>
							<Link to="/courses">Browse Courses</Link>
						</Button>
					</div>
				</div>
			</section>
		</main>
	);
}
